import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PAYMENT_METHODS, allocateOldestFirst, parseMoney, type PaymentMethod } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { resolveParty, searchParties } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { titleCase, useFormat, useToday } from "@/lib/format";
import { useSession } from "@/lib/session";
import { Alert, Button, Card, CardTitle, Detail, Field, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

/**
 * Recording a payment.
 *
 * The highest-frequency action in the product, so the required path is three fields and a
 * button: who paid, how much, when. Allocation defaults to the oldest open invoice first — what
 * both sides expect, and what clears the debt that is accruing late fees — and is only shown
 * expanded when the manager wants to override it.
 */

interface OpenInvoice {
  id: string;
  number: string;
  dueDate: string;
  total: number;
  paid: number;
  status: string;
  unitLabel: string | null;
}

export function PaymentNewPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { vocab } = useSession();
  const fmt = useFormat();
  const today = useToday();

  const [partyId, setPartyId] = useState(search.get("partyId") ?? "");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("bank_transfer");
  const [receivedOn, setReceivedOn] = useState(today);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [manual, setManual] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  useEffect(() => setReceivedOn(today), [today]);

  const { data: open, isFetching: loadingInvoices } = useQuery({
    queryKey: ["open-invoices", partyId],
    queryFn: () => api.get<{ invoices: OpenInvoice[]; today: string }>(`/payments/open-invoices/${partyId}`),
    enabled: Boolean(partyId),
  });

  const amountMinor = parseMoney(amount) ?? 0;
  const invoices = open?.invoices ?? [];

  /** The same allocator the API uses, so the preview here is what actually gets written. */
  const automatic = useMemo(
    () =>
      allocateOldestFirst(
        amountMinor,
        invoices.map((invoice) => ({
          id: invoice.id,
          dueDate: invoice.dueDate,
          total: invoice.total,
          paid: invoice.paid,
          status: invoice.status as never,
        })),
      ),
    [amountMinor, invoices],
  );

  const manualAllocations = useMemo(
    () =>
      Object.entries(overrides)
        .map(([invoiceId, value]) => ({ invoiceId, amount: parseMoney(value) ?? 0 }))
        .filter((allocation) => allocation.amount > 0),
    [overrides],
  );

  const allocations = manual ? manualAllocations : automatic.allocations;
  const allocated = allocations.reduce((sum, allocation) => sum + allocation.amount, 0);
  const credit = amountMinor - allocated;

  const record = useApiMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/payments", {
        partyId,
        amount: amountMinor,
        method,
        receivedOn,
        reference: reference || null,
        notes: notes || null,
        leaseId: search.get("leaseId") || null,
        allocations: manual ? manualAllocations : undefined,
      }),
    invalidates: [["payments"], ["invoices"], ["dashboard"], ["party", partyId], ["open-invoices", partyId]],
    onSuccess: (data) => navigate(`/app/payments/${data.id}`),
  });

  const outstanding = invoices.reduce((sum, invoice) => sum + (invoice.total - invoice.paid), 0);

  return (
    <div>
      <PageHeader
        title="Record a payment"
        back={{ to: "/app/payments", label: "Payments" }}
        subtitle="Cash at the door, a transfer from the bank, or a POS receipt. A numbered receipt is issued on save."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardTitle>The payment</CardTitle>
          <div className="space-y-4">
            {record.errorMessage ? <Alert tone="danger">{record.errorMessage}</Alert> : null}

            <Field label="Who paid" htmlFor="partyId" required hint="Search by name, phone or email.">
              <SearchSelect
                id="partyId"
                value={partyId}
                onChange={(next) => setPartyId(next)}
                search={searchParties()}
                resolve={resolveParty}
                placeholder="Search for a client…"
                emptyMessage="No client matched."
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Amount" htmlFor="amount" required>
                <MoneyInput
                  id="amount"
                  value={amount}
                  onChange={setAmount}
                  placeholder="1,500,000"
                  autoFocus
                />
              </Field>
              <Field label="Received on" htmlFor="receivedOn" required>
                <Input id="receivedOn" type="date" value={receivedOn} onChange={(event) => setReceivedOn(event.target.value)} />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Method" htmlFor="method">
                <Select id="method" value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}>
                  {PAYMENT_METHODS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Reference"
                htmlFor="reference"
                hint={PAYMENT_METHODS.find((m) => m.value === method)?.needsReference ? "Transfer or POS reference." : "Optional."}
              >
                <Input id="reference" placeholder="Teller or transfer reference" value={reference} onChange={(event) => setReference(event.target.value)} />
              </Field>
            </div>

            <Field label="Notes" htmlFor="notes">
              <Input id="notes" placeholder="Anything worth recording." value={notes} onChange={(event) => setNotes(event.target.value)} />
            </Field>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button disabled={!partyId || amountMinor <= 0 || record.isPending} onClick={() => record.mutate(undefined as never)}>
                {record.isPending ? "Saving…" : "Record payment"}
              </Button>
              <LinkButton to="/app/payments" variant="secondary">
                Cancel
              </LinkButton>
            </div>
          </div>
        </Card>

        <Card>
          <CardTitle
            hint={
              manual
                ? "Type an amount against each invoice. Anything left over becomes credit."
                : "Oldest first — it clears the debt that is accruing late fees."
            }
            action={
              invoices.length > 0 ? (
                <button type="button" className="link" onClick={() => setManual((value) => !value)}>
                  {manual ? "Use automatic" : "Allocate by hand"}
                </button>
              ) : null
            }
          >
            Where it goes
          </CardTitle>

          {!partyId ? (
            <p className="py-6 text-center text-sm text-ink-500">Choose a client to see what they owe.</p>
          ) : loadingInvoices ? (
            <Spinner />
          ) : invoices.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-sm text-ink-500">This client has no open {vocab.invoices}.</p>
              <p className="mt-1 text-xs text-ink-500">The payment will be held as credit and offered against the next one.</p>
            </div>
          ) : (
            <>
              <dl className="mb-3">
                <Detail label="They owe">{fmt.money(outstanding)}</Detail>
                <Detail label="Allocated">{fmt.money(allocated)}</Detail>
                <Detail label="Left as credit">
                  <span className={credit > 0 ? "font-medium text-info-700" : ""}>{fmt.money(Math.max(0, credit))}</span>
                </Detail>
              </dl>

              <Table
                head={
                  <tr>
                    <th>{titleCase(vocab.invoice)}</th>
                    <th>Due</th>
                    <th className="num">Owing</th>
                    <th className="num">Applying</th>
                  </tr>
                }
              >
                {invoices.map((invoice) => {
                  const owing = invoice.total - invoice.paid;
                  const applied = allocations.find((allocation) => allocation.invoiceId === invoice.id)?.amount ?? 0;
                  return (
                    <tr key={invoice.id}>
                      <td>
                        {invoice.number}
                        {invoice.unitLabel ? <span className="block text-xs text-ink-400">{invoice.unitLabel}</span> : null}
                      </td>
                      <td className="text-ink-500">{fmt.date(invoice.dueDate)}</td>
                      <td className="num tabular">{fmt.money(owing)}</td>
                      <td className="num">
                        {manual ? (
                          <MoneyInput
                            className="w-28 text-right"
                            value={overrides[invoice.id] ?? ""}
                            placeholder="0"
                            onChange={(next) => setOverrides((current) => ({ ...current, [invoice.id]: next }))}
                          />
                        ) : (
                          <span className={`tabular ${applied > 0 ? "font-medium text-brand-700" : "text-ink-300"}`}>
                            {applied > 0 ? fmt.money(applied) : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Table>

              {credit < 0 ? (
                <div className="mt-3">
                  <Alert tone="danger">You have allocated more than the amount received.</Alert>
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
